/**
 * App display preferences (theme, font size, compact mode). Lives on the Settings page.
 * Changes persist to localStorage and apply app-wide immediately.
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Button,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { usePreferences } from '../../contexts/PreferencesContext';
import { UserPreferences, DEFAULT_PREFERENCES } from '../../types/preferences';

export default function PreferencesSettings() {
  const { preferences, setPreferences, updatePreference } = usePreferences();

  const handleChange = <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
    updatePreference(key, value);
  };

  const handleReset = () => {
    setPreferences({ ...DEFAULT_PREFERENCES });
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SettingsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Preferences</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Display settings. Changes apply immediately and are saved automatically.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PaletteIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Display</Typography>
          </Box>
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Theme</FormLabel>
            <RadioGroup
              value={preferences.theme}
              onChange={(e) => handleChange('theme', e.target.value as UserPreferences['theme'])}
            >
              <FormControlLabel value="light" control={<Radio />} label="Light" />
              <FormControlLabel value="dark" control={<Radio />} label="Dark" />
              <FormControlLabel value="auto" control={<Radio />} label="Auto (system)" />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ mb: 3, display: 'block' }}>
            <FormLabel component="legend">Font size</FormLabel>
            <RadioGroup
              value={preferences.fontSize}
              onChange={(e) => handleChange('fontSize', e.target.value as UserPreferences['fontSize'])}
            >
              <FormControlLabel value="small" control={<Radio />} label="Small" />
              <FormControlLabel value="medium" control={<Radio />} label="Medium" />
              <FormControlLabel value="large" control={<Radio />} label="Large" />
            </RadioGroup>
          </FormControl>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.compactMode}
                  onChange={(e) => handleChange('compactMode', e.target.checked)}
                />
              }
              label="Compact mode"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Tighter layout and smaller controls
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Button variant="outlined" onClick={handleReset}>
            Reset to defaults
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
